import React, { useState } from 'react';

import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { useMy } from '@/hooks';
import { useScanProfiles } from '@/hooks/useScanProfiles';
import { RateLimitingForm } from '@/sections/settings/components';
import { SingleScanProfileForm } from '@/sections/settings/components/forms/SingleScanProfileForm';
import type { ScanProfileFooterState } from '@/sections/settings/components/primitives/scanProfileFooterState';
import { SettingCompactCard } from '@/sections/settings/components/primitives/SettingCompactCard';
import { RATE_LIMIT_SYSTEM_DEFAULTS } from '@/sections/settings/constants';
import { formatInheritLabel } from '@/sections/settings/utils/formatInherit';
import { ScanProfile } from '@/types/scanProfile';

interface ScanIntensitySectionProps {
  email?: string;
  editable?: boolean;
}

type ModalState =
  | { kind: 'none' }
  | { kind: 'list' }
  | { kind: 'default' }
  | { kind: 'override-create' }
  | { kind: 'override-edit'; profile: ScanProfile };

function formatHostsValue(
  hosts: number | undefined,
  inherited: number
): string {
  return hosts === undefined ? formatInheritLabel(inherited) : `${hosts}`;
}

function formatRateValue(rate: number | undefined, inherited: number): string {
  return rate === undefined
    ? formatInheritLabel(inherited, 'rq/s')
    : `${rate} rq/s`;
}

// ---------------------------------------------------------------------------
// ScanIntensityListModal — shown when overrides exist
// ---------------------------------------------------------------------------

interface ScanIntensityListModalProps {
  open: boolean;
  onClose: () => void;
  globalHosts: number;
  globalRate: number;
  profiles: ScanProfile[];
  editable: boolean;
  onEditDefault: () => void;
  onEditOverride: (profile: ScanProfile) => void;
  onAddOverride: () => void;
}

const ScanIntensityListModal: React.FC<ScanIntensityListModalProps> = ({
  open,
  onClose,
  globalHosts,
  globalRate,
  profiles,
  editable,
  onEditDefault,
  onEditOverride,
  onAddOverride,
}) => {
  return (
    <Modal
      title="Edit Scan Intensity"
      open={open}
      onClose={onClose}
      size="4xl"
      className="px-8 py-6"
      footer={
        editable
          ? {
              text: 'Add Profile',
              onClick: onAddOverride,
              'data-testid': 'scan-intensity-list-add-override',
            }
          : { hideCancelButton: true }
      }
      data-testid="scan-intensity-list-modal"
    >
      <div className="flex flex-col" data-testid="scan-intensity-list-modal">
        {/* Default row */}
        <div
          className="border-chariot-stroke flex items-center justify-between gap-4 border-b px-1 py-3"
          data-testid="scan-intensity-list-row-default"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-chariot-text-secondary text-xs font-semibold tracking-wider uppercase">
              Default
            </span>
            <span className="text-chariot-text-secondary text-sm">
              {globalHosts} hosts &middot; {globalRate} rq/s
            </span>
          </div>
          {editable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onEditDefault}
              data-testid="scan-intensity-list-edit-button-default"
            >
              Edit
            </Button>
          )}
        </div>

        {/* Override rows */}
        {profiles.map(profile => (
          <div
            key={profile.id}
            className="border-chariot-stroke flex items-center justify-between gap-4 border-b px-1 py-3 last:border-b-0"
            data-testid={`scan-intensity-list-row-${profile.id}`}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span
                className="text-chariot-text-primary truncate text-sm font-medium"
                title={profile.name}
              >
                {profile.name}
              </span>
              <span className="text-chariot-text-secondary text-xs">
                {formatHostsValue(profile.simultaneousHosts, globalHosts)} hosts
                &middot;{' '}
                {formatRateValue(profile.capabilityRateLimit, globalRate)}
              </span>
            </div>
            {editable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEditOverride(profile)}
                data-testid={`scan-intensity-list-edit-button-${profile.id}`}
              >
                Edit
              </Button>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// ScanIntensitySection — outer card + modal orchestration
// ---------------------------------------------------------------------------

export const ScanIntensitySection: React.FC<ScanIntensitySectionProps> = ({
  email,
  editable = true,
}) => {
  const { data: customerSettings } = useMy(
    { resource: 'setting' },
    {
      email,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  const rateLimitSetting = React.useMemo(() => {
    const setting = customerSettings?.find(s => s.name === 'rate-limit')
      ?.value as
      | { simultaneousHosts?: number; capabilityRateLimit?: number }
      | undefined;
    return setting;
  }, [customerSettings]);

  const globalHosts =
    rateLimitSetting?.simultaneousHosts ??
    RATE_LIMIT_SYSTEM_DEFAULTS.SIMULTANEOUS_HOSTS;
  const globalRate =
    rateLimitSetting?.capabilityRateLimit ??
    RATE_LIMIT_SYSTEM_DEFAULTS.REQUESTS_PER_HOST;

  const { profiles } = useScanProfiles(email);

  const [modalState, setModalState] = useState<ModalState>({ kind: 'none' });
  const [overrideFooter, setOverrideFooter] =
    useState<ScanProfileFooterState | null>(null);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleEditClick = () => {
    setModalState({ kind: 'list' });
  };

  const handleCloseAll = () => setModalState({ kind: 'none' });

  // From list modal: edit default row
  const handleListEditDefault = () => setModalState({ kind: 'default' });

  // From list modal: edit an override row
  const handleListEditOverride = (profile: ScanProfile) =>
    setModalState({ kind: 'override-edit', profile });

  // From list modal or card empty button: add new override
  const handleAddOverride = () => setModalState({ kind: 'override-create' });

  // Close override create/edit — reset footer state too
  const handleCloseOverride = () => {
    setModalState({ kind: 'none' });
    setOverrideFooter(null);
  };

  // ---------------------------------------------------------------------------
  // Derived state for rendering
  // ---------------------------------------------------------------------------

  const isListOpen = modalState.kind === 'list';
  const isDefaultOpen = modalState.kind === 'default';
  const isOverrideOpen =
    modalState.kind === 'override-create' ||
    modalState.kind === 'override-edit';

  const overrideModalProfile =
    modalState.kind === 'override-edit' ? modalState.profile : undefined;
  const overrideModalMode: 'create' | 'edit' =
    modalState.kind === 'override-edit' ? 'edit' : 'create';

  return (
    <>
      {/* Outer compact card — matches Scan Level / Source IP / Schedule pattern */}
      <SettingCompactCard data-testid="scan-intensity-section">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            <h4 className="font-bold">Scan Intensity</h4>
          </div>
          <span className="text-chariot-text-secondary text-xs">
            Control how aggressively your assets are scanned
          </span>
        </div>

        {/* Edit button on right side */}
        {editable && (
          <div className="absolute top-1/2 right-4 flex -translate-y-1/2 flex-col items-end gap-1">
            {/* Main Edit button — opens default form (0 overrides) or list modal (1+ overrides) */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEditClick}
              data-testid="scan-intensity-edit-button"
            >
              Edit
            </Button>
          </div>
        )}
      </SettingCompactCard>

      {/* List modal — shown when 1+ overrides exist */}
      <ScanIntensityListModal
        open={isListOpen}
        onClose={handleCloseAll}
        globalHosts={globalHosts}
        globalRate={globalRate}
        profiles={profiles}
        editable={editable}
        onEditDefault={handleListEditDefault}
        onEditOverride={handleListEditOverride}
        onAddOverride={handleAddOverride}
      />

      {/* Default (rate-limiting) modal */}
      <Modal
        title="Edit Default Scan Intensity"
        open={isDefaultOpen}
        onClose={handleCloseAll}
        size="4xl"
        className="px-8 py-6"
        hideFooter
        data-testid="scan-intensity-default-modal"
      >
        <RateLimitingForm customerEmail={email} onClose={handleCloseAll} />
      </Modal>

      {/* Override create/edit modal — wizard handles step 1 (settings) and step 2 (assets) */}
      <Modal
        title={
          overrideModalMode === 'create'
            ? 'Add Override'
            : `Edit Override: ${overrideModalProfile?.name ?? ''}`
        }
        open={isOverrideOpen}
        onClose={handleCloseOverride}
        size="4xl"
        className="px-8 py-6"
        bodyClassName="min-h-[min(820px,calc(100vh-4rem))]"
        footer={
          overrideFooter
            ? {
                text: overrideFooter.primary.label,
                onClick: overrideFooter.primary.onClick,
                disabled: overrideFooter.primary.disabled,
                'data-testid': overrideFooter.primary['data-testid'],
                back: overrideFooter.back,
                left: overrideFooter.left,
              }
            : undefined
        }
        data-testid="scan-intensity-override-modal"
      >
        {isOverrideOpen &&
          (overrideModalMode === 'edit' && overrideModalProfile ? (
            <SingleScanProfileForm
              key={overrideModalProfile.id}
              customerEmail={email}
              globalRateLimit={{
                simultaneousHosts: globalHosts,
                capabilityRateLimit: globalRate,
              }}
              mode="edit"
              profile={overrideModalProfile}
              onClose={handleCloseOverride}
              onFooterStateChange={setOverrideFooter}
            />
          ) : (
            <SingleScanProfileForm
              key="create"
              customerEmail={email}
              globalRateLimit={{
                simultaneousHosts: globalHosts,
                capabilityRateLimit: globalRate,
              }}
              mode="create"
              onClose={handleCloseOverride}
              onFooterStateChange={setOverrideFooter}
            />
          ))}
      </Modal>
    </>
  );
};
